import { ApplicationConfig, Injectable, isDevMode, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { provideTransloco, TRANSLOCO_TRANSPILER } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { UnauthInterceptor } from './shared/interceptors/unauth.interceptor';
import { provideDialogZoneFix } from './shared/providers/dialog-zone-fix.provider';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';

import localeEn from '@angular/common/locales/en';
import localeBg from '@angular/common/locales/bg';
import { MessageFormatTranspiler, provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';

// Transloco passes 'bg_BG' on language change, but MessageFormat's plural rules need 'bg-BG'
@Injectable()
class LocaleMessageFormatTranspiler extends MessageFormatTranspiler {
    override onLangChanged(lang: string): void {
        super.onLangChanged(lang.replace('_', '-'));
    }
}

// Supported languages by Angular Pipes
registerLocaleData(localeEn);
registerLocaleData(localeBg);

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideDialogZoneFix(),
        provideRouter(
            routes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                // scrollPositionRestoration: 'enabled',
            }),
        ),
        provideClientHydration(),
        provideHttpClient(withFetch()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthInterceptor,
            multi: true
        }, {
            provide: LOCALE_ID,
            useValue: environment.locales.default.replace('_', '-'),
        },
        provideTransloco({
            config: {
                availableLangs: environment.locales.available?.map((item: any) => {
                    return {
                        id: item.id ?? '',
                        label: item.label ?? '',
                    }
                }),
                defaultLang: environment?.locales?.default ?? 'bg_BG',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,

        }),
        provideTranslocoMessageformat({
            locales: environment.locales.available?.map((item: any) => item.id.replace('_', '-')),
        }),
        { provide: TRANSLOCO_TRANSPILER, useClass: LocaleMessageFormatTranspiler },
        MessageService,
        providePrimeNG({
            theme: {
                preset: definePreset(Lara, {
                    semantic: {
                        primary: {
                            50: '{blue.50}',
                            100: '{blue.100}',
                            200: '{blue.200}',
                            300: '{blue.300}',
                            400: '{blue.400}',
                            500: '{blue.500}',
                            600: '{blue.600}',
                            700: '{blue.700}',
                            800: '{blue.800}',
                            900: '{blue.900}',
                            950: '{blue.950}'
                        }
                    }
                }),
                options: {
                    darkModeSelector: false,
                    order: 'primeng, app-styles'
                }
            }
        })
    ]
};
